import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { FormSubmission, OptimizedFormSubmissionService as FormSubmissionService } from '../services/optimizedFormSubmissionService';
import { userService } from '../services/userService';
import CommonPageHeader from '../components/common/CommonPageHeader';
import SearchBar from '../components/common/SearchBar';
import LoadingScreen from '../components/common/LoadingScreen';
import { 
  DocumentTextIcon,
  EyeIcon,
  CheckIcon,
  XMarkIcon,
  ClockIcon,
  FunnelIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

type FilterStatus = 'all' | 'draft' | 'submitted' | 'approved' | 'rejected' | 'processing';

const FormSubmissions: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState<FormSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('all');
  const [selectedSubmission, setSelectedSubmission] = useState<FormSubmission | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject'>('approve');
  const [userNames, setUserNames] = useState<Record<string, string>>({});

  // Permissions
  const canViewAll = hasPermission('forms', 'admin');
  const canReview = hasPermission('forms', 'admin') || hasPermission('forms', 'access');

  useEffect(() => {
    loadSubmissions();
  }, []);

  useEffect(() => {
    filterSubmissions();
  }, [submissions, searchTerm, statusFilter]);

  useEffect(() => {
    loadUserNames();
  }, [submissions]);

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      let loadedSubmissions: FormSubmission[];
      
      if (canViewAll) {
        // Admin can see all submissions in their organization
        const result = await FormSubmissionService.getSubmissions({
          formTemplateId: '', // Will be filtered client-side
          organizationId: user?.organizationId
        });
        loadedSubmissions = result.submissions;
      } else {
        // Regular users can only see their own submissions
        const result = await FormSubmissionService.getUserSubmissions(
          user?.uid || '',
          { organizationId: user?.organizationId }
        );
        loadedSubmissions = result.submissions;
      }
      
      setSubmissions(loadedSubmissions);
    } catch (error) {
      console.error('Error loading submissions:', error);
      toast.error(t('formSubmissions.loadError') || 'Error loading form submissions');
    } finally {
      setLoading(false);
    }
  };

  const filterSubmissions = () => {
    let filtered = submissions;

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(submission =>
        submission.templateName?.toLowerCase().includes(term) ||
        submission.submittedByName?.toLowerCase().includes(term) ||
        submission.id.toLowerCase().includes(term)
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(submission => submission.status === statusFilter);
    }

    setFilteredSubmissions(filtered);
  };

  const loadUserNames = async () => {
    const uniqueUserIds = Array.from(new Set(submissions.map(s => s.submittedBy)));
    const newUserNames: Record<string, string> = {};

    for (const userId of uniqueUserIds) {
      if (!userNames[userId]) {
        try {
          const userData = await userService.getUser(userId);
          if (userData) {
            newUserNames[userId] = userData.displayName || userData.email || 'Unknown User';
          } else {
            newUserNames[userId] = 'Unknown User';
          }
        } catch (error) {
          console.error('Error loading user data:', error);
          newUserNames[userId] = 'Unknown User';
        }
      }
    }

    if (Object.keys(newUserNames).length > 0) {
      setUserNames(prev => ({ ...prev, ...newUserNames }));
    }
  };

  const handleReview = async () => {
    if (!selectedSubmission || !canReview) return;

    try {
      if (reviewAction === 'approve') {
        await FormSubmissionService.approveSubmission(selectedSubmission.id);
        toast.success(t('formSubmissions.approveSuccess') || 'Submission approved');
      } else {
        await FormSubmissionService.rejectSubmission(selectedSubmission.id);
        toast.success(t('formSubmissions.rejectSuccess') || 'Submission rejected');
      }

      setShowReviewModal(false);
      setSelectedSubmission(null);
      setReviewNotes('');
      loadSubmissions();
    } catch (error) {
      console.error('Error reviewing submission:', error);
      toast.error(t('formSubmissions.reviewError') || 'Error reviewing submission');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'submitted':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'approved':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft':
        return <ClockIcon className="h-4 w-4" />;
      case 'submitted':
        return <DocumentTextIcon className="h-4 w-4" />;
      case 'approved':
        return <CheckIcon className="h-4 w-4" />;
      case 'rejected':
        return <XMarkIcon className="h-4 w-4" />;
      case 'processing':
        return <ArrowPathIcon className="h-4 w-4" />;
      default:
        return <DocumentTextIcon className="h-4 w-4" />;
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-6">
      <CommonPageHeader
        title={t('formSubmissions.title') || 'Form Submissions'}
        subtitle={t('formSubmissions.subtitle') || 'Manage and review form submissions'}
        icon={DocumentTextIcon}
      />

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder={t('formSubmissions.searchPlaceholder') || 'Search submissions...'}
            />
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-5 w-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as FilterStatus)}
                className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="all">{t('formSubmissions.allStatuses') || 'All Statuses'}</option>
                <option value="draft">{t('formSubmissions.draft') || 'Draft'}</option>
                <option value="submitted">{t('formSubmissions.submitted') || 'Submitted'}</option>
                <option value="approved">{t('formSubmissions.approved') || 'Approved'}</option>
                <option value="rejected">{t('formSubmissions.rejected') || 'Rejected'}</option>
                <option value="processing">{t('formSubmissions.processing') || 'Processing'}</option>
              </select>
            </div>
            
            <button
              onClick={loadSubmissions}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md hover:bg-gray-50 dark:hover:bg-gray-500"
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              {t('common.refresh') || 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Submissions List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        {filteredSubmissions.length === 0 ? (
          <div className="p-8 text-center">
            <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              {t('formSubmissions.noSubmissions') || 'No submissions found'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {searchTerm || statusFilter !== 'all'
                ? (t('formSubmissions.noResults') || 'No submissions match your filters')
                : (t('formSubmissions.noSubmissionsYet') || 'No form submissions have been created yet')
              }
            </p>
          </div>
        ) : (
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t('formSubmissions.form') || 'Form'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t('formSubmissions.submittedBy') || 'Submitted By'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t('formSubmissions.status') || 'Status'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t('formSubmissions.submittedAt') || 'Submitted'}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t('formSubmissions.actions') || 'Actions'}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredSubmissions.map((submission) => (
                  <tr key={submission.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {submission.templateName}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        ID: {submission.id.slice(-8)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {submission.submittedByName || userNames[submission.submittedBy] || 'Unknown User'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(submission.status)}`}>
                        {getStatusIcon(submission.status)}
                        <span className="ml-1 capitalize">{submission.status}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(submission.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => navigate(`/form-submissions/${submission.id}`)}
                          className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        
                        {canReview && submission.status === 'submitted' && (
                          <>
                            <button
                              onClick={() => {
                                setSelectedSubmission(submission);
                                setReviewAction('approve');
                                setShowReviewModal(true);
                              }}
                              className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                            >
                              <CheckIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedSubmission(submission);
                                setReviewAction('reject');
                                setShowReviewModal(true);
                              }}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Review Modal */}
      {showReviewModal && selectedSubmission && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                {reviewAction === 'approve' 
                  ? (t('formSubmissions.approveSubmission') || 'Approve Submission')
                  : (t('formSubmissions.rejectSubmission') || 'Reject Submission')
                }
              </h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('formSubmissions.reviewNotes') || 'Review Notes (Optional)'}
                </label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder={t('formSubmissions.reviewNotesPlaceholder') || 'Add notes about your decision...'}
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowReviewModal(false);
                    setSelectedSubmission(null);
                    setReviewNotes('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md hover:bg-gray-50 dark:hover:bg-gray-500"
                >
                  {t('common.cancel') || 'Cancel'}
                </button>
                <button
                  onClick={handleReview}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                    reviewAction === 'approve'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {reviewAction === 'approve' 
                    ? (t('formSubmissions.approve') || 'Approve')
                    : (t('formSubmissions.reject') || 'Reject')
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormSubmissions;
